import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Button,
  TextField,
  Tabs,
  Tab,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  ContentCopy as ContentCopyIcon,
  Download as DownloadIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { RootState, AppDispatch } from '../../store';
import {
  fetchMerchantProfile,
  updateMerchantProfile,
  fetchAPIKeys,
  generateAPIKey,
  setWebhookUrl,
  uploadVerificationDocument,
  MerchantProfile,
} from '../../store/slices/merchantSlice';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const MerchantSettingsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { profile, apiKeys, loadingProfile, loadingApiKeys } = useSelector(
    (state: RootState) => state.merchant
  );

  const [tabValue, setTabValue] = useState(0);
  const [copiedKeyId, setCopiedKeyId] = useState<string | null>(null);
  const [openApiKeyDialog, setOpenApiKeyDialog] = useState(false);
  const [apiKeyName, setApiKeyName] = useState('');
  const [generatingKey, setGeneratingKey] = useState(false);

  const [profileForm, setProfileForm] = useState<Partial<MerchantProfile>>({});
  const [webhookUrl, setWebhookUrlInput] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingWebhook, setSavingWebhook] = useState(false);

  useEffect(() => {
    dispatch(fetchMerchantProfile());
    dispatch(fetchAPIKeys());
  }, [dispatch]);

  useEffect(() => {
    if (profile) {
      setProfileForm({
        businessName: profile.businessName,
        email: profile.email,
        phoneNumber: profile.phoneNumber,
        address: profile.address,
        city: profile.city,
        country: profile.country,
        businessType: profile.businessType,
        taxId: profile.taxId,
      });
      if ((profile as any).webhookUrl) setWebhookUrlInput((profile as any).webhookUrl);
    }
  }, [profile]);

  const handleSaveProfile = async () => {
    try {
      setSavingProfile(true);
      await dispatch(updateMerchantProfile(profileForm)).unwrap();
      alert('Profile updated successfully!');
    } catch (error) {
      alert('Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSaveWebhook = async () => {
    try {
      setSavingWebhook(true);
      await (dispatch as any)(setWebhookUrl(webhookUrl)).unwrap();
      alert('Webhook URL updated successfully!');
    } catch (error) {
      alert('Failed to update webhook URL');
    } finally {
      setSavingWebhook(false);
    }
  };

  const handleGenerateAPIKey = async () => {
    if (!apiKeyName) {
      alert('Please enter a key name');
      return;
    }

    try {
      setGeneratingKey(true);
      await dispatch(generateAPIKey(apiKeyName)).unwrap();
      setOpenApiKeyDialog(false);
      setApiKeyName('');
      dispatch(fetchAPIKeys());
    } catch (error) {
      alert('Failed to generate API key');
    } finally {
      setGeneratingKey(false);
    }
  };

  const handleCopyKey = (key: string, keyId: string) => {
    navigator.clipboard.writeText(key);
    setCopiedKeyId(keyId);
    setTimeout(() => setCopiedKeyId(null), 2000);
  };

  const [uploadDocType, setUploadDocType] = useState('cac_certificate');

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append('document', file);
      formData.append('documentType', uploadDocType);

      await dispatch(uploadVerificationDocument(formData)).unwrap();
      alert('Document uploaded successfully!');
    } catch (error) {
      alert('Failed to upload document');
    }
  };

  if (loadingProfile && !profile) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{
          fontWeight: 700,
          color: '#202647',
          fontSize: { xs: '1.5rem', md: '2rem' },
        }}>
          Merchant Settings
        </Typography>
        <Typography variant="body2" sx={{ color: '#7F839C', mt: 0.5 }}>
          Configure your merchant account and API keys
        </Typography>
      </Box>

      {/* Tabs */}
      <Card sx={{ borderRadius: 3 }}>
        <Box sx={{ borderBottom: '1px solid #E8E8F0' }}>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                color: '#7F839C',
              },
              '& .Mui-selected': {
                color: '#11C1EC',
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#11C1EC',
              },
            }}
          >
            <Tab label="Profile" id="settings-tab-0" />
            <Tab label="Webhook" id="settings-tab-1" />
            <Tab label="API Keys" id="settings-tab-2" />
            <Tab label="Documents" id="settings-tab-3" />
          </Tabs>
        </Box>

        {/* Profile Tab */}
        <TabPanel value={tabValue} index={0}>
          <CardContent sx={{ pt: 0 }}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Business Name"
                  value={profileForm.businessName || ''}
                  onChange={(e) => setProfileForm({ ...profileForm, businessName: e.target.value })}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={profileForm.email || ''}
                  onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={profileForm.phoneNumber || ''}
                  onChange={(e) => setProfileForm({ ...profileForm, phoneNumber: e.target.value })}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Business Type"
                  value={profileForm.businessType || ''}
                  onChange={(e) => setProfileForm({ ...profileForm, businessType: e.target.value })}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Address"
                  value={profileForm.address || ''}
                  onChange={(e) => setProfileForm({ ...profileForm, address: e.target.value })}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="City"
                  value={profileForm.city || ''}
                  onChange={(e) => setProfileForm({ ...profileForm, city: e.target.value })}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Country"
                  value={profileForm.country || ''}
                  onChange={(e) => setProfileForm({ ...profileForm, country: e.target.value })}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Tax ID"
                  value={profileForm.taxId || ''}
                  onChange={(e) => setProfileForm({ ...profileForm, taxId: e.target.value })}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: { xs: 'stretch', sm: 'flex-start' } }}>
                  <Button
                    variant="contained"
                    onClick={handleSaveProfile}
                    disabled={savingProfile}
                    sx={{ flex: { xs: 1, sm: 'auto' } }}
                  >
                    {savingProfile ? 'Saving...' : 'Save Changes'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </TabPanel>

        {/* Webhook Tab */}
        <TabPanel value={tabValue} index={1}>
          <CardContent sx={{ pt: 0 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              Configure your webhook URL to receive real-time notifications for payment events.
            </Alert>
            <TextField
              fullWidth
              label="Webhook URL"
              value={webhookUrl}
              onChange={(e) => setWebhookUrlInput(e.target.value)}
              placeholder="https://yoursite.com/webhooks/malpay"
              multiline
              rows={2}
              sx={{ mb: 3 }}
            />
            <Button
              variant="contained"
              onClick={handleSaveWebhook}
              disabled={savingWebhook || !webhookUrl}
            >
              {savingWebhook ? 'Saving...' : 'Update Webhook URL'}
            </Button>
          </CardContent>
        </TabPanel>

        {/* API Keys Tab */}
        <TabPanel value={tabValue} index={2}>
          <CardContent sx={{ pt: 0 }}>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <Typography variant="body2" sx={{ color: '#7F839C' }}>
                Manage your API keys for server-to-server integration
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpenApiKeyDialog(true)}
              >
                Generate Key
              </Button>
            </Box>

            {loadingApiKeys ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress />
              </Box>
            ) : apiKeys.length === 0 ? (
              <Card sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" sx={{ color: '#7F839C' }}>
                  No API keys generated yet. Create one to get started.
                </Typography>
              </Card>
            ) : (
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#F8F8FC' }}>
                        <TableCell sx={{ fontWeight: 700, color: '#202647' }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#202647' }}>Key</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#202647' }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#202647' }}>Last Used</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#202647' }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {apiKeys.map((key) => (
                        <TableRow key={key.id}>
                          <TableCell sx={{ color: '#565972', fontWeight: 500 }}>
                            {key.name}
                          </TableCell>
                          <TableCell sx={{ color: '#565972', fontSize: '0.85rem', fontFamily: 'monospace' }}>
                            {key.key.substring(0, 10)}...
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="caption"
                              sx={{
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 2,
                                backgroundColor: key.isActive ? '#E8F8F4' : '#FFE8E8',
                                color: key.isActive ? '#009A6E' : '#E6303F',
                                fontWeight: 600,
                              }}
                            >
                              {key.isActive ? 'Active' : 'Inactive'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ color: '#7F839C', fontSize: '0.85rem' }}>
                            {key.lastUsed ? new Date(key.lastUsed).toLocaleDateString() : 'Never'}
                          </TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => handleCopyKey(key.key, key.id)}
                              title="Copy Key"
                            >
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* Mobile Card View */}
            <Grid container spacing={2} sx={{ display: { xs: 'flex', md: 'none' } }}>
              {apiKeys.map((key) => (
                <Grid size={{ xs: 12 }} key={key.id}>
                  <Card sx={{ borderRadius: 3 }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#202647', mb: 1 }}>
                        {key.name}
                      </Typography>
                      <Grid container spacing={1.5} sx={{ mb: 2 }}>
                        <Grid size={{ xs: 6 }}>
                          <Typography variant="caption" sx={{ color: '#7F839C' }}>Status</Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              mt: 0.5,
                              px: 1,
                              py: 0.25,
                              borderRadius: 1,
                              backgroundColor: key.isActive ? '#E8F8F4' : '#FFE8E8',
                              color: key.isActive ? '#009A6E' : '#E6303F',
                              fontWeight: 600,
                              width: 'fit-content',
                            }}
                          >
                            {key.isActive ? 'Active' : 'Inactive'}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <Typography variant="caption" sx={{ color: '#7F839C' }}>Last Used</Typography>
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#565972' }}>
                            {key.lastUsed ? new Date(key.lastUsed).toLocaleDateString() : 'Never'}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Box sx={{
                        p: 1,
                        backgroundColor: '#F8F8FC',
                        borderRadius: 1,
                        mb: 1.5,
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        overflow: 'auto',
                      }}>
                        {key.key}
                      </Box>
                      <Button
                        fullWidth
                        size="small"
                        startIcon={<ContentCopyIcon fontSize="small" />}
                        onClick={() => handleCopyKey(key.key, key.id)}
                      >
                        {copiedKeyId === key.id ? 'Copied!' : 'Copy'}
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </TabPanel>

        {/* Documents Tab */}
        <TabPanel value={tabValue} index={3}>
          <CardContent sx={{ pt: 0 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              Upload verification documents to complete your KYC requirements.
            </Alert>

            <TextField
              select
              fullWidth
              label="Document type"
              value={uploadDocType}
              onChange={(e) => setUploadDocType(e.target.value)}
              sx={{ mb: 2 }}
              SelectProps={{ native: true }}
            >
              <option value="cac_certificate">CAC certificate</option>
              <option value="tin_document">TIN document</option>
              <option value="proof_of_address">Proof of address</option>
              <option value="utility_bill">Utility bill</option>
              <option value="directors_id">Director's ID</option>
            </TextField>

            <Box sx={{
              border: '2px dashed #11C1EC',
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              backgroundColor: 'rgba(17, 193, 236, 0.05)',
              cursor: 'pointer',
              mb: 3,
            }}>
              <input
                type="file"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
                id="doc-upload"
              />
              <label htmlFor="doc-upload" style={{ cursor: 'pointer', display: 'block' }}>
                <DownloadIcon sx={{ fontSize: 40, color: '#11C1EC', mb: 1 }} />
                <Typography variant="body2" sx={{ color: '#202647', fontWeight: 600, mb: 0.5 }}>
                  Click to upload document
                </Typography>
                <Typography variant="caption" sx={{ color: '#7F839C' }}>
                  Supported formats: PDF, JPG, PNG (max 10MB)
                </Typography>
              </label>
            </Box>

            {profile?.verificationDocuments && profile.verificationDocuments.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#202647', mb: 1.5 }}>
                  Uploaded Documents
                </Typography>
                {profile.verificationDocuments.map((doc: any, index: number) => (
                  <Card key={index} sx={{ mb: 1.5, backgroundColor: '#F8F8FC' }}>
                    <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1.5 }}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#202647' }}>
                          {doc.documentType}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#7F839C' }}>
                          Uploaded: {new Date(doc.uploadedAt).toLocaleDateString()}
                        </Typography>
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 2,
                          backgroundColor: doc.status === 'approved' ? '#E8F8F4' : doc.status === 'rejected' ? '#FFE8E8' : '#FFF3E0',
                          color: doc.status === 'approved' ? '#009A6E' : doc.status === 'rejected' ? '#E6303F' : '#E6A03E',
                          fontWeight: 600,
                        }}
                      >
                        {doc.status}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}
          </CardContent>
        </TabPanel>
      </Card>

      {/* Generate API Key Dialog */}
      <Dialog
        open={openApiKeyDialog}
        onClose={() => setOpenApiKeyDialog(false)}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#202647', bgcolor: '#F8F8FC' }}>
          Generate New API Key
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <TextField
            fullWidth
            label="Key Name"
            value={apiKeyName}
            onChange={(e) => setApiKeyName(e.target.value)}
            placeholder="e.g., Production, Staging"
            helperText="Choose a descriptive name for this API key"
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #E8E8F0' }}>
          <Button onClick={() => setOpenApiKeyDialog(false)} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleGenerateAPIKey}
            variant="contained"
            disabled={!apiKeyName || generatingKey}
          >
            {generatingKey ? 'Generating...' : 'Generate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MerchantSettingsPage;
